<template>
  <div id="page-business-view">
    <vs-alert color="danger" title="Business Not Found" :active.sync="businessNotFound">
      <span>Business record with id: {{ $route.params.id }} not found. </span>
      <span>
        <span>Check </span
        ><router-link :to="{ name: 'system-business-list' }" class="text-inherit underline"
          >All Businesses</router-link
        >
      </span>
    </vs-alert>

    <div id="business-data" v-if="businessData">
      <vx-card :title="$t('businesses.business')" class="mb-base">
        <div class="vx-row">
          <div class="vx-col flex-1">
            <table>
              <tr>
                <td class="font-semibold">{{ $t('entity.state') }}</td>
                <td>{{ $t('entity.states.' + businessData.state) }}</td>
              </tr>
              <tr>
                <td class="font-semibold">{{ $t('businesses.website') }}</td>
                <td>{{ businessData.website }}</td>
              </tr>
              <tr v-if="businessData.contracts">
                <td class="font-semibold">{{ $t('businesses.contracts') }}</td>
                <td>{{ businessData.contracts.join(', ') }}</td>
              </tr>
            </table>
          </div>

          <div class="vx-col flex-1">
            <table>
              <tr>
                <td class="font-semibold">{{ $t('businesses.plans.plan') }}</td>
                <td>{{ $t('businesses.plans.' + businessData.plan) }}</td>
              </tr>
              <tr>
                <td class="font-semibold">{{ $t('businesses.expiration_date') }}</td>
                <td>
                  {{
                    $moment(businessData.expirationDate)
                      .locale($vs.rtl ? 'fa' : 'en')
                      .format('dddd, DD MMMM YYYY')
                  }}
                </td>
              </tr>
              <tr v-if="businessData.agreements">
                <td class="font-semibold">{{ $t('businesses.agreements') }}</td>
                <td>{{ businessData.agreements.join(', ') }}</td>
              </tr>
            </table>
          </div>

          <div class="vx-col w-full flex">
            <vs-button
              icon-pack="feather"
              icon="icon-edit"
              class="mr-4"
              :to="{ name: 'system-business-edit', params: { Id: $route.params.id } }"
              >{{ $t('forms.edit') }}</vs-button
            >
            <vs-button
              type="border"
              color="danger"
              icon-pack="feather"
              icon="icon-trash"
              @click="confirmDeleteRecord"
              >{{ $t('forms.delete') }}</vs-button
            >
          </div>
        </div>
      </vx-card>

      <div class="vx-row">
        <div class="vx-col lg:w-1/2 w-full">
          <vx-card :title="$t('businesses.manager')" class="mb-base">
            <div class="vx-row">
              <div class="vx-col" id="avatar-col">
                <div class="img-container mb-4">
                  <vs-avatar :src="avatar" size="80px" class="mr-1" />
                </div>
              </div>
              <div class="vx-col flex-1">
                <table>
                  <tr>
                    <td class="font-semibold">{{ $t('users.first_name') }}</td>
                    <td>
                      <router-link :to="{ name: 'system-user-view', params: { id: businessData.user.id } }">
                        {{ businessData.user.details.firstName }}
                        {{ businessData.user.details.lastName }}
                      </router-link>
                    </td>
                  </tr>
                  <tr>
                    <td class="font-semibold">{{ $t('users.username') }}</td>
                    <td>{{ businessData.user.username }}</td>
                  </tr>
                  <tr>
                    <td class="font-semibold">{{ $t('users.genders.gender') }}</td>
                    <td>{{ businessData.user.details.gender }}</td>
                  </tr>
                </table>
              </div>
            </div>
          </vx-card>
        </div>

        <div class="vx-col lg:w-1/2 w-full">
          <vx-card :title="$t('entity.connections')" class="mb-base">
            <table>
              <tr v-if="businessData.groups">
                <td class="font-semibold">{{ $t('entity.groups') }}</td>
                <td>{{ businessData.groups.join(', ') }}</td>
              </tr>
              <tr v-if="businessData.tags">
                <td class="font-semibold">{{ $t('entity.tags') }}</td>
                <td>{{ businessData.tags.join(', ') }}</td>
              </tr>
              <tr v-if="businessData.partners">
                <td class="font-semibold">{{ $t('entity.partners') }}</td>
                <td>{{ businessData.partners.join(', ') }}</td>
              </tr>
            </table>
          </vx-card>
        </div>

        <vx-card :title="$t('places.places')" class="mb-base mx-5">
          <div class="vx-row">
            <div class="vx-col w-full">
              <vs-table ref="table" :data="businessData.places" class="vs-table places-table">
                <template slot="thead">
                  <vs-th sort-key="alias">{{ $t('places.alias') }}</vs-th>
                  <vs-th sort-key="subject">{{ $t('places.subject') }}</vs-th>
                  <vs-th sort-key="category">{{ $t('places.category') }}</vs-th>
                  <vs-th sort-key="state">{{ $t('entity.state') }}</vs-th>
                  <vs-th sort-key="status">{{ $t('places.status') }}</vs-th>
                  <vs-th sort-key="province">{{ $t('users.addresses.address') }}</vs-th>
                </template>

                <template slot-scope="{ data }">
                  <tbody v-if="data.length">
                    <vs-tr :data="tr" :key="indextr" v-for="(tr, indextr) in data">
                      <vs-td :data="tr.alias">
                        {{ tr.alias }}
                      </vs-td>
                      <vs-td :data="tr.subject">
                        {{ $t(`places.subjects.${tr.subject}`) }}
                      </vs-td>
                      <vs-td :data="tr.category">
                        {{ $t(`places.categories.${tr.category}`) }}
                      </vs-td>
                      <vs-td :data="tr.state">
                        {{ $t(`entity.states.${tr.state}`) }}
                      </vs-td>
                      <vs-td :data="tr.status">
                        {{ $t(`places.statuses.${tr.status}`) }}
                      </vs-td>
                      <vs-td>
                        <address-string :address="tr" hide-label />
                      </vs-td>

                      <template slot="expand">
                        <div class="w-full">
                          <div class="vx-row w-full">
                            <div class="vx-col w-full md:w-1/4">
                              <div class="font-semibold">{{ $t('users.location') }}:</div>
                              <div>{{ $t('users.lat') }}: {{ tr.location.lat }}</div>
                              <div>{{ $t('users.lon') }}: {{ tr.location.lon }}</div>
                            </div>
                            <div class="vx-col w-full md:w-3/4">
                              <open-street-map
                                view-only
                                :markers="[{ latlng: tr.location }]"
                                :zoom="15"
                                disable-fit
                                :center="tr.location"
                              />
                            </div>
                          </div>
                          <div class="vx-row w-full" v-if="tr.description">
                            <div class="vx-col">
                              <div class="font-semibold mt-2">{{ $t('places.description') }}:</div>
                              <div>{{ tr.description }}</div>
                            </div>
                          </div>
                        </div>
                      </template>
                    </vs-tr>
                  </tbody>
                </template>
              </vs-table>
            </div>
          </div>
        </vx-card>

        <vx-card :title="$t('employees.employees')" class="mb-base mx-5">
          <business-employee-list :business="$route.params.id" />
        </vx-card>
      </div>
    </div>
  </div>
</template>

<script>
import OpenStreetMap from '@/views/components/maps/OpenStreetMap'
import AddressString from '@/views/components/maps/AddressString'
import moduleBusiness from '@/store/business/moduleBusiness'
import BusinessEmployeeList from './BusinessEmployeeList'

export default {
  name: 'BusinessView',
  components: {
    OpenStreetMap,
    AddressString,
    BusinessEmployeeList
  },
  data() {
    return {
      businessData: null,
      businessNotFound: false,
      avatar: null
    }
  },
  computed: {
    businessAddress() {
      let str = ''
      for (const field in this.businessData.location) {
        str += `${field} `
      }
      return str
    }
  },
  methods: {
    confirmDeleteRecord() {
      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: this.$t('forms.are_you_sure'),
        text: this.$t('forms.confirm_delete', {
          title: `${this.$t('businesses.business')} "${this.businessData.id}"`
        }),
        accept: this.deleteRecord,
        acceptText: this.$t('forms.delete'),
        cancelText: this.$t('forms.cancel')
      })
    },
    deleteRecord() {
      this.$store.dispatch('business/removeBusiness', this.businessData.id).then(() => {
        this.showDeleteSuccess()
        this.$router.push({ name: 'system-business-list' })
      })
    },
    showDeleteSuccess() {
      this.$vs.notify({
        color: 'success',
        title: this.$t('forms.successfully_deleted')
      })
    }
  },
  created() {
    // Register Business Module
    if (!moduleBusiness.isRegistered) {
      this.$store.registerModule('business', moduleBusiness)
      moduleBusiness.isRegistered = true
    }

    const businessId = this.$route.params.id
    this.$store
      .dispatch('business/fetchBusiness', businessId)
      .then(res => {
        this.businessData = res.data.business
        //get user avatar
        if (this.businessData.user.avatar) {
          this.$store.dispatch('file/downloadFile', this.businessData.user.avatar).then(response => {
            this.avatar = response
          })
        }
      })
      .catch(err => {
        if (err.response.status === 404) {
          this.businessNotFound = true
        }
      })
  }
}
</script>

<style lang="scss">
#avatar-col {
  width: 10rem;
}

#page-business-view {
  table:not(.vs-table) {
    td {
      vertical-align: top;
      min-width: 140px;
      padding-bottom: 0.8rem;
      word-break: break-all;
    }
  }
}
.td-check.active-expanded .vs-icon {
  -webkit-transform: translate(5px) rotate(0deg) !important;
  transform: translate(5px) rotate(0deg) !important;
}
</style>
